import { Subscription, fromEvent, from, merge } from "rxjs";
// Angular
import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
// Layout
import {
	LayoutConfigService,
	SplashScreenService,
	TranslationService,
} from "./core/_base/layout";
// language list
import { locale as enLang } from "./core/_config/i18n/en";
import { locale as chLang } from "./core/_config/i18n/ch";
import { locale as esLang } from "./core/_config/i18n/es";
import { locale as jpLang } from "./core/_config/i18n/jp";
import { locale as deLang } from "./core/_config/i18n/de";
import { locale as frLang } from "./core/_config/i18n/fr";
import { Store } from "@ngrx/store";
import { AppState } from "./core/reducers";
import { ExtendLogoutTimer } from "./core/auth/_actions/auth.actions";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { filter } from "rxjs/operators";
import { environment } from "environments/environment";
import { AuthorizationService } from 'app/core/ana/authorization';

@Component({
  standalone: false,
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
	// Keen/Metronic: keep kt-root on the app shell (was on <body> when root was body[kt-root])
	@HostBinding("attr.kt-root") ktRootAttr = "";

	// Public properties
	title = "Metronic";
	loader: boolean;
	updateCheckText: string;
	private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param translationService: TranslationService
	 * @param router: Router
	 * @param layoutConfigService: LayoutCongifService
	 * @param splashScreenService: SplashScreenService
	 */
	constructor(
		private translationService: TranslationService,
		private router: Router,
		private layoutConfigService: LayoutConfigService,
		private splashScreenService: SplashScreenService,
		private authService: AuthorizationService,
		private store: Store<AppState>,
		private updates: SwUpdate
	) {
		// register translations
		this.translationService.loadTranslations(
			enLang,
			chLang,
			esLang,
			jpLang,
			deLang,
			frLang
		);
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		// enable/disable loader
		this.loader = this.layoutConfigService.getConfig("loader.enabled");

		const routerSubscription = this.router.events.subscribe(async (event) => {
			if (event instanceof NavigationEnd) {
				// hide splash screen
				this.splashScreenService.hide();

				// scroll to top on every route change
				window.scrollTo(0, 0);
				// if(event.url != "/auth/login" && event.url != "/auth/forgot-password" )
				// {
				// 	await this.authService.verifyClosedSession().toPromise().then(auth => {
                //         if (auth) {
                           
                //             this.router.navigateByUrl('/auth/login');
                //         }

                //     });
				// }
				 
				
				// to display back the body content
				setTimeout(() => {
					document.body.classList.add("kt-page--loaded");
				}, 500);

				this.trackPageView(event.urlAfterRedirects || event.url);
			}
		});
		this.unsubscribe.push(routerSubscription);

		const keyDown = fromEvent(document, "keydown");
		const scroll = fromEvent(document, "scroll");
		const touchmove = fromEvent(document, "mousemove");
		const click = fromEvent(document, "click");
		const allEvents = merge(keyDown, scroll, touchmove, click);
		const events = allEvents.subscribe((event) => {
			this.store.dispatch(new ExtendLogoutTimer());
		});
		this.unsubscribe.push(events);

		this.checkUpdates();

		this.initGoogleAnalytics();
	}

	/**
	 * On Destroy
	 */
	ngOnDestroy() {
		this.unsubscribe.forEach((sb) => sb.unsubscribe());
	}

	/**
	 * Force it to reload the page from the server whenever a new app version is available.
	 */
	private checkUpdates() {
		this.updates
			.checkForUpdate()
			.then(() => (this.updateCheckText = "resolved"))
			.catch(
				(err) => (this.updateCheckText = `rejected: ${err.message}`)
			);
		this.updates.versionUpdates
			.pipe(filter((evt): evt is VersionReadyEvent => evt.type === "VERSION_READY"))
			.subscribe(() => {
				this.updates
					.activateUpdate()
					.then(() => document.location.reload());
			});
	}
	public loadExternalScript(url: string) {
		const body = <HTMLDivElement>document.head;
		const script = document.createElement("script");
		script.innerHTML = "";
		script.src = url;
		script.async = true;
		body.appendChild(script);
	}

	public loadScriptWithContent(content: string) {
		const body = <HTMLDivElement>document.head;
		const script = document.createElement("script");
		script.innerHTML = content;
		body.appendChild(script);
	}

	private initGoogleAnalytics(): void {
		if (!environment.production || !environment.googleAnalyticsMeasurementId) {
			return;
		}

		const measurementId = environment.googleAnalyticsMeasurementId;
		this.loadExternalScript(
			`https://www.googletagmanager.com/gtag/js?id=${measurementId}`
		);
		const content: string =
			`window.dataLayer = window.dataLayer || [];` +
			`function gtag(){dataLayer.push(arguments);}` +
			`gtag('js', new Date());` +
			`gtag('config', '${measurementId}', { send_page_view: false });`;
		this.loadScriptWithContent(content);
		this.trackPageView(this.router.url);
	}

	private trackPageView(url: string): void {
		if (!environment.production || !environment.googleAnalyticsMeasurementId) {
			return;
		}

		const gtagFn = (window as any).gtag;
		if (typeof gtagFn === "function") {
			gtagFn("event", "page_view", {
				page_path: url,
				page_location: window.location.href,
				page_title: document.title,
			});
		}
	}
}
